/**
 * Demo-frame fixtures for the Transactions view (`AccountTransactionHistory`).
 *
 * This is the fetch-boundary mock for distributions — the same shape the
 * YaaS Client Console uses (`services/sim/mockApi.ts`). `fetchDistributions`
 * in `hooks/yaas/useDistributions.ts` calls `demoDistributions()` before it
 * builds a request; on a hit it returns the fixture and never touches
 * yaas-api. Everything above that call — the hook, the list, the rows, the
 * loading / empty / error states — is byte-identical between a demo frame
 * and the real thing. Nothing is threaded through props, and no component
 * reads `?state=`.
 *
 * Frames (see `helpers/demoState.ts`):
 *   active           — 12 weekly distributions, latest 2 days ago, on the
 *                      $500 position the frame renders (≈ $1.01 / cycle).
 *   just-distributed — 12 weekly distributions, latest landed today, on the
 *                      $12,450.75 position (≈ $25 / cycle).
 *   opted-in         — day zero of an opt-in Program: Earning, but no
 *                      distribution behind it yet. Empty list.
 *   pending          — applied, position not yet activated. Empty list.
 *
 * Every other frame — and no frame at all — returns `null`, so the request
 * goes to yaas-api exactly as before. `?state=below-min` is the frame to use
 * for the error state: it renders a position (so the footer's "See account
 * transaction history" link is there) but is deliberately not mocked, so the
 * list falls through to the real fetch and shows the retry copy when the API
 * is unreachable.
 *
 * Fixtures are hand-authored literals, not generated — the same twelve rows
 * with the same amounts on every reload. Only the dates move, and only at
 * day granularity (anchored to UTC midnight) so the list stays plausibly
 * recent without ever reshuffling within a session.
 *
 * PRODUCTION SAFETY: `demoDistributions` returns `null` immediately when
 * `IS_PRODUCTION_BUILD` is true, before it reads the URL. A production
 * bundle therefore cannot serve a fixture even if a user types `?state=`.
 */

import { IS_PRODUCTION_BUILD } from "@shared/utils/environment";
import type { Distribution, DistributionsResponse } from "@solomon-apps/shared";

import { readDemoState } from "../helpers/demoState";

/** Simulated network latency (ms) so the list's skeleton still renders. */
const MOCK_LATENCY_MS = 400;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CYCLE_DAYS = 7;

/** The retail USDv pool every retail distribution settles against. */
const RETAIL_POOL = {
    uuid: "00000000-0000-4000-8000-0000000000aa",
    name: "usdv-wallet",
    address: "EnViUgEjqL6HzLRxLDnxTb4t5xukumJYpNwq6fxF1tJYY",
    displayName: "USDv Balance"
};

/** One authored cycle: what was paid, and the balance it was paid on. */
interface CycleFixture {
    amount: string;
    balanceAtDistribution: string;
    /** `null` reproduces a row with no on-chain link (plain, non-clickable). */
    transactionId: string | null;
}

// ~10.5% APY over a 7-day cycle ≈ 0.2% of balance. Balances drift down
// going backwards (rewards compound) with one visible step where the user
// topped the position up, so the "on $X" column isn't a flat line.
const ACTIVE_CYCLES: CycleFixture[] = [
    { amount: "1.01", balanceAtDistribution: "500.00", transactionId: "3xQm7Ktz9vP2sN4bR8hW1cY6dL5gT0aF" },
    { amount: "1.00", balanceAtDistribution: "498.99", transactionId: "5nBv2Rq8mK3wX7tZ1yH4pC9sD6fG0jL" },
    { amount: "1.00", balanceAtDistribution: "497.99", transactionId: "8kLp4Wc1nT6vB9xQ2mR5zY7hJ3dS0gA" },
    { amount: "0.99", balanceAtDistribution: "496.99", transactionId: "2mZx9Hs5bV3kQ8wN1cP7tR4yF6gD0lJ" },
    { amount: "0.99", balanceAtDistribution: "496.00", transactionId: "7vTq3Nm8cX2bK5wR9zH1pY4sL6dG0fB" },
    // Top-up week — balance steps from $295 to $496 after this cycle.
    { amount: "0.60", balanceAtDistribution: "295.01", transactionId: null },
    { amount: "0.59", balanceAtDistribution: "294.41", transactionId: "4hRw7Ck2mB9xV1nQ6tZ3yP8sJ5dL0gF" },
    { amount: "0.59", balanceAtDistribution: "293.82", transactionId: "9pNs1Vt6kM4bX8wC3zR7yH2qL5dG0jT" },
    { amount: "0.59", balanceAtDistribution: "293.23", transactionId: "6cKm8Bw3nZ1vT5xQ9hR2yP7sF4dL0gV" },
    { amount: "0.58", balanceAtDistribution: "292.64", transactionId: "1tWq5Hn9cV2bM7xK4zR6yP3sJ8dL0gN" },
    { amount: "0.58", balanceAtDistribution: "292.06", transactionId: "8bVx2Mk6nT9wC1zQ5hR4yP7sL3dG0jW" },
    { amount: "0.58", balanceAtDistribution: "291.48", transactionId: "3nQz7Vw1cK8bM5xT2yR9hP4sD6fL0gB" }
];

const JUST_DISTRIBUTED_CYCLES: CycleFixture[] = [
    { amount: "25.07", balanceAtDistribution: "12450.75", transactionId: "7mKx3Bq9nV2wT5cZ1yR8hP4sJ6dL0gF" },
    { amount: "25.02", balanceAtDistribution: "12425.68", transactionId: "2vNw8Ck1mB6xQ9tZ3yH5pR7sL4dG0jT" },
    { amount: "24.97", balanceAtDistribution: "12400.66", transactionId: "9hTz4Vm7cX1bK6wN2yR8pP3sD5fL0gQ" },
    { amount: "24.92", balanceAtDistribution: "12375.69", transactionId: "5bQw1Nk8mV3xT7cZ9yH2pR6sJ4dL0gM" },
    { amount: "24.87", balanceAtDistribution: "12350.77", transactionId: "1cMz6Bw4nT8vK2xQ7yR5hP9sF3dL0gV" },
    { amount: "24.82", balanceAtDistribution: "12325.90", transactionId: "6wVq2Hm9cN5bX8tZ4yR1pP7sJ3dG0kB" },
    // Top-up week — balance steps from $9,990 to $12,325 after this cycle.
    { amount: "20.13", balanceAtDistribution: "9990.00", transactionId: null },
    { amount: "20.09", balanceAtDistribution: "9969.87", transactionId: "4kBx7Nw2mV9cT1zQ6yH3pR8sL5dG0jZ" },
    { amount: "20.05", balanceAtDistribution: "9949.78", transactionId: "8tCq5Mk1nB4wX9zV2yR7hP6sJ3dL0gN" },
    { amount: "20.01", balanceAtDistribution: "9929.73", transactionId: "3zNv9Hw6cK2bM8xT1yR4pP5sD7fL0gQ" },
    { amount: "19.97", balanceAtDistribution: "9909.72", transactionId: "7qWm4Bt8nV1cZ5xK9yR2hP6sJ3dL0gC" },
    { amount: "19.93", balanceAtDistribution: "9889.75", transactionId: "2fKz8Nq3mB7wV4xT6yR1pP9sL5dG0jH" }
];

/** Today at UTC midnight — the anchor every fixture date counts back from. */
function todayUtcMidnight(): number {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

/**
 * Turns authored cycles into `Distribution` records, newest first, one
 * `CYCLE_DAYS` step apart starting `latestOffsetDays` before today.
 */
function buildDistributions(cycles: CycleFixture[], latestOffsetDays: number): Distribution[] {
    const anchor = todayUtcMidnight();
    return cycles.map((cycle, index) => {
        const date = new Date(anchor - (latestOffsetDays + index * CYCLE_DAYS) * MS_PER_DAY).toISOString();
        return {
            // Descending so the newest row carries the highest id, as the
            // API's auto-increment would.
            id: 4200 - index,
            distributionDate: date,
            amount: cycle.amount,
            transactionId: cycle.transactionId,
            balanceAtDistribution: cycle.balanceAtDistribution,
            createdAt: date,
            pool: RETAIL_POOL
        };
    });
}

function pageOf(all: Distribution[], page: number, limit: number): DistributionsResponse {
    const start = Math.max(0, (page - 1) * limit);
    return {
        data: all.slice(start, start + limit),
        total: all.length,
        limit,
        page
    };
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
}

/**
 * Resolves the fixture for the active `?state=` frame, or `null` when the
 * frame has no fixture (so the caller performs the real fetch).
 *
 * Synchronous and side-effect free — `demoDistributions` wraps it with the
 * simulated latency.
 */
export function demoDistributionsResponse(page: number, limit: number): DistributionsResponse | null {
    if (IS_PRODUCTION_BUILD) return null;

    const state = readDemoState();
    if (state === "active") {
        return pageOf(buildDistributions(ACTIVE_CYCLES, 2), page, limit);
    }
    if (state === "just-distributed") {
        return pageOf(buildDistributions(JUST_DISTRIBUTED_CYCLES, 0), page, limit);
    }
    if (state === "opted-in" || state === "pending" || state === "gold-lp" || state === "gold-lp-wallet") {
        return pageOf([], page, limit);
    }
    return null;
}

/**
 * Fetch-boundary hook for `fetchDistributions`. Returns the demo fixture
 * after a short simulated latency (so the list's loading skeleton renders),
 * or `null` when no demo frame is active.
 */
export async function demoDistributions(page: number, limit: number): Promise<DistributionsResponse | null> {
    const response = demoDistributionsResponse(page, limit);
    if (response === null) return null;
    await delay(MOCK_LATENCY_MS);
    return response;
}
